import { useEffect, useRef, useState } from 'react';

import { IS_SYSTEM_THEME, USER_THEME, USER_THEME_DARK, USER_THEME_LIGHT } from 'apps/auth/constants/Constants';
import { getCurrentSystemTheme } from 'apps/auth/helpers/utils';
import { useCookies } from 'apps/auth/hooks/useCookies';

type ThemeName = string | undefined;
type ChangeListenerEvent = { name?: string; value?: string | boolean };

type ThemeSetter = (theme: string, isSystemTheme: boolean) => void;

export const useThemeDetector = (): { theme: string; setTheme: ThemeSetter; isSystemTheme: boolean } => {
    const cookies = useCookies();

    const initialTheme = cookies.get(USER_THEME) as ThemeName;
    const initialIsSystemTheme = cookies.get(IS_SYSTEM_THEME) as boolean | undefined;
    const [theme, setTheme] = useState<ThemeName>(initialTheme);
    const [isSystemTheme, setIsSystemTheme] = useState<boolean>(initialIsSystemTheme ?? true);

    const isSystemThemeRef = useRef<boolean>(isSystemTheme);
    isSystemThemeRef.current = isSystemTheme;

    const setCurrentTheme: ThemeSetter = (nextTheme, isSystem) => {
        cookies.remove(USER_THEME, { path: '/' });
        cookies.set(USER_THEME, nextTheme, { path: '/' });
        setTheme(nextTheme);

        cookies.remove(IS_SYSTEM_THEME, { path: '/' });
        cookies.set(IS_SYSTEM_THEME, isSystem, { path: '/' });
        setIsSystemTheme(isSystem);
    };

    const onSystemThemeChange = (event: MediaQueryListEvent): void => {
        if (!isSystemThemeRef.current) {
            return;
        }
        const currentTheme = event.matches ? USER_THEME_DARK : USER_THEME_LIGHT;
        setCurrentTheme(currentTheme, true);
    };

    const onCookiesThemeChange = (event: ChangeListenerEvent): void => {
        if (event.name === USER_THEME && typeof event.value === 'string') {
            setTheme(event.value);
        }

        if (event.name === IS_SYSTEM_THEME && event.value !== undefined) {
            const isSystem = Boolean(event.value);
            setIsSystemTheme(isSystem);
            isSystemThemeRef.current = isSystem;
        }
    };

    useEffect(() => {
        if (!__isBrowser__ || typeof window === 'undefined') {
            return;
        }

        const darkThemeMedia = window.matchMedia?.('(prefers-color-scheme: dark)');

        if (darkThemeMedia?.addEventListener) {
            darkThemeMedia.addEventListener('change', onSystemThemeChange);
        } else {
            darkThemeMedia?.addListener(onSystemThemeChange);
        }

        cookies.addChangeListener(onCookiesThemeChange);

        if (isSystemThemeRef.current) {
            setCurrentTheme(getCurrentSystemTheme(), true);
        }

        return () => {
            if (darkThemeMedia?.removeEventListener) {
                darkThemeMedia.removeEventListener('change', onSystemThemeChange);
            } else {
                darkThemeMedia?.removeListener(onSystemThemeChange);
            }

            cookies.removeChangeListener(onCookiesThemeChange);
        };
    }, []);

    return { theme: theme ?? USER_THEME_LIGHT, setTheme: setCurrentTheme, isSystemTheme };
};
